// compressImage.js — shared product-photo compressor (single + bulk upload).
//
// Decode fallback (Brian, 2026-08-25): createImageBitmap is the browser's
// FAST decoder and rejects some perfectly good JPEGs (unusual color
// profiles / CMYK / certain editor exports) that display fine everywhere —
// 18 of Brian's 74 bulk photos hit this. When it refuses, fall back to a
// plain <img> decode, the same path the catalog uses to display photos, so
// anything the catalog could show can also be uploaded.
async function decodeImage(file) {
  try {
    return await createImageBitmap(file);
  } catch {
    return await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("The source image could not be decoded — re-save it as JPG (Paint: File → Save as) and retry."));
      };
      img.src = url;
    });
  }
}

export async function compressImage(file, maxWidth = 800, quality = 0.75) {
  const img = await decodeImage(file);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const scale = Math.min(1, maxWidth / w);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext("2d");
  // JPEG has no transparency — unpainted pixels turn BLACK on export
  // (Brian, 2026-08-25: transparent PNGs got black backgrounds). White
  // first, so transparency becomes white like the catalog cards.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  if (typeof img.close === "function") img.close(); // free the bitmap
  return new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", quality));
}
